import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { JamInventraLogoFull } from '../components/JamInventraLogo';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    organization_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, password, first_name, last_name, organization_name } = formData;
    
    if (!email || !password || !first_name || !last_name || !organization_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(formData);
      toast.success('Account created! Your 14-day free trial has started.');
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 bg-white">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#0A0A0A]/60 hover:text-[#0A0A0A] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          
          <div className="mb-8">
            <JamInventraLogoFull height={50} />
          </div>
          
          <h1 className="text-heading-black text-4xl text-[#0A0A0A] tracking-tight mb-2">
            START FREE TRIAL
          </h1>
          <p className="text-[#0A0A0A]/60 mb-8">
            30 days free. No credit card required.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-sm font-medium text-[#0A0A0A]">Owner First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="John"
                  className="h-11 border-[#E5E5EA] focus:border-[#007AFF] focus:ring-[#007AFF]"
                  data-testid="register-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-sm font-medium text-[#0A0A0A]">Owner Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Doe"
                  className="h-11 border-[#E5E5EA] focus:border-[#007AFF] focus:ring-[#007AFF]"
                  data-testid="register-lastname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization_name" className="text-sm font-medium text-[#0A0A0A]">Business Name</Label>
              <Input
                id="organization_name"
                name="organization_name"
                value={formData.organization_name}
                onChange={handleChange}
                placeholder="My Business Ltd."
                className="h-11 border-[#E5E5EA] focus:border-[#007AFF] focus:ring-[#007AFF]"
                data-testid="register-company-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#0A0A0A]">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@business.com"
                className="h-11 border-[#E5E5EA] focus:border-[#007AFF] focus:ring-[#007AFF]"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-[#0A0A0A]">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="876-xxx-xxxx"
                className="h-11 border-[#E5E5EA] focus:border-[#007AFF] focus:ring-[#007AFF]"
                data-testid="register-phone-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#0A0A0A]">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="h-11 border-[#E5E5EA] focus:border-[#007AFF] focus:ring-[#007AFF]"
                data-testid="register-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-[#007AFF] hover:bg-[#0066D6] text-white btn-hover"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#0A0A0A]/60">
            Already have an account?{' '}
            <Link to="/login" className="text-[#007AFF] hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/4481256/pexels-photo-4481256.jpeg')" }}
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/60" />
        <div className="absolute inset-0 flex items-end p-12">
          <div>
            <p className="text-overline text-[#007AFF] mb-2">30-DAY FREE TRIAL</p>
            <h2 className="text-heading-black text-3xl text-white tracking-tight">
              TAKE CONTROL OF<br />YOUR OPERATIONS
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
